import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Bug, Sparkles, HelpCircle, MessageSquare, Loader2, CheckCircle2 } from "lucide-react";
import { FeedbackAPI } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { toast } from "sonner";

/**
 * FeedbackDialog — single-step form that ships to the support inbox.
 *
 * Fields: kind (Bug/Feature/Question/Other), subject, message.
 * Auto-attached: route, viewport, user-agent, theme, timezone, app version.
 *
 * The diagnostic block is collected ONLY at submit time so we don't capture
 * a stale snapshot when the dialog mounts.
 */
const KIND_OPTIONS = [
  { value: "bug", label: "Bug", Icon: Bug },
  { value: "feature", label: "Feature", Icon: Sparkles },
  { value: "question", label: "Question", Icon: HelpCircle },
  { value: "other", label: "Other", Icon: MessageSquare },
];

const MAX_SUBJECT = 140;
const MAX_MESSAGE = 4000;
const APP_VERSION = process.env.REACT_APP_VERSION || "v1.3";

function collectContext(resolvedTheme) {
  try {
    return {
      route: typeof window !== "undefined" ? window.location.pathname + window.location.search : null,
      user_agent: navigator?.userAgent || null,
      app_version: APP_VERSION,
      viewport:
        typeof window !== "undefined"
          ? `${window.innerWidth}x${window.innerHeight}`
          : null,
      theme: resolvedTheme || null,
      timezone:
        typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
    };
  } catch {
    return null;
  }
}

export default function FeedbackDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const [kind, setKind] = useState("bug");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind("bug");
    setSubject("");
    setMessage("");
    setSubmitting(false);
    setSent(false);
  }, [open]);

  const canSubmit = useMemo(
    () => subject.trim().length >= 2 && message.trim().length >= 2 && !submitting,
    [subject, message, submitting]
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await FeedbackAPI.send({
        kind,
        subject: subject.trim(),
        message: message.trim(),
        context: collectContext(resolvedTheme),
      });
      setSent(true);
      toast.success("Feedback sent — thank you.");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e?.message || "Could not send feedback. Try again in a moment."
      );
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] sm:max-w-[500px]"
        data-testid="feedback-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light tracking-tight">
            {sent ? "Thanks so much for taking the time to share your thoughts! Your feedback is incredibly valuable to us as we shape MileageIQ." : "Send feedback"}
          </DialogTitle>
          <DialogDescription className="text-[var(--text-muted)] text-sm leading-relaxed">
            {sent
              ? "We’re reading every note that comes in, and we’ll get back to you within one business day if we have questions or need to follow up. Feel free to just hit reply if you have anything else to add!"
              : "Bugs, ideas, or anything that felt off. Replies will be received to the e-mail on file with your account."}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-4" data-testid="feedback-sent">
            <div className="flex items-start gap-3 px-3 py-3 border border-[var(--border-subtle)] bg-[var(--bg-elevated)]" style={{ borderRadius: 4 }}>
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
              <div className="text-sm leading-relaxed">
                <div className="text-[var(--text-primary)]">Your message was delivered.</div>
                {user?.email && (
                  <div className="text-[12px] text-[var(--text-muted)] mt-1">
                    Replies will land in <span className="text-[var(--text-primary)]">{user.email}</span>.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="label-tiny">Type</Label>
              <div
                className="inline-flex w-full items-center bg-[var(--bg-input)] border border-[var(--border-subtle)] p-0.5"
                style={{ borderRadius: 2 }}
                data-testid="feedback-kind-toggle"
              >
                {KIND_OPTIONS.map(({ value, label, Icon }) => {
                  const active = kind === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setKind(value)}
                      data-testid={`feedback-kind-${value}`}
                      aria-pressed={active}
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs tracking-wider transition-colors ${
                        active
                          ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]"
                          : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                      }`}
                      style={{ borderRadius: 2 }}
                    >
                      <Icon className="w-3 h-3" strokeWidth={1.75} />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-tiny">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value.slice(0, MAX_SUBJECT))}
                placeholder="Short summary"
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)]"
                data-testid="feedback-subject-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="label-tiny">Message</Label>
                <span className="text-[11px] tracking-wider text-[var(--text-faint)] font-mono-data">
                  {message.length}/{MAX_MESSAGE}
                </span>
              </div>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
                placeholder={
                  kind === "bug"
                    ? "What happened? What did you expect?"
                    : kind === "feature"
                    ? "What would this enable for you?"
                    : "Tell us more…"
                }
                rows={6}
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] resize-none"
                data-testid="feedback-message-input"
              />
            </div>

            <div className="text-[11px] text-[var(--text-faint)] leading-relaxed">
              We auto-attach your email, the current page, browser version and timezone — saves you the typing if we need to debug.
            </div>
          </div>
        )}

        <DialogFooter>
          {sent ? (
            <Button
              onClick={() => onOpenChange(false)}
              className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
              data-testid="feedback-done-btn"
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                className="bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
                data-testid="feedback-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
                data-testid="feedback-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={2} />
                    Sending…
                  </>
                ) : (
                  "Send feedback"
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
